import React, { useEffect, useRef, useState } from 'react';
import Footer from '../components/Footer';
import axios from 'axios';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const JOBS_PER_PAGE = 6;

const pagerButtonCls = 'rounded-full border border-yellow-200 bg-white px-4 py-2 text-sm font-bold text-yellow-700 transition hover:bg-yellow-50 disabled:cursor-not-allowed disabled:opacity-40';

// Page numbers around the current page, with '…' gaps: 1 … 4 5 6 … 109
const getPageWindow = (current, total) => {
  const pages = [];
  for (let n = 1; n <= total; n += 1) {
    if (n === 1 || n === total || Math.abs(n - current) <= 1) pages.push(n);
    else if (pages[pages.length - 1] !== '…') pages.push('…');
  }
  return pages;
};

const TEXT_FILTERS = [
  { name: 'title', label: 'Job Title', placeholder: 'Type a job title, e.g. Accountant' },
  { name: 'department', label: 'Department', placeholder: 'Type a department' },
  { name: 'section', label: 'Section', placeholder: 'Type a section' },
  { name: 'district', label: 'District', placeholder: 'Type a district, e.g. Palakkad' },
  { name: 'location', label: 'Location', placeholder: 'Type a town or place' }
];

const initialFilters = {
  keyword: '',
  title: '',
  department: '',
  section: '',
  district: '',
  location: '',
  salaryMin: '',
  salaryMax: ''
};

const SALARY_PRESETS = [
  { label: 'Up to ₹10k', min: '', max: '10000' },
  { label: '₹10k – 20k', min: '10000', max: '20000' },
  { label: '₹20k – 30k', min: '20000', max: '30000' },
  { label: '₹30k +', min: '30000', max: '' }
];

const KEYWORD_FIELDS = ['title', 'company_firm_organisation', 'company', 'location', 'district', 'department', 'section', 'qualification', 'experience', 'description'];

const includesText = (value, query) => String(value || '').toLowerCase().includes(query.trim().toLowerCase());

// "15,000 - 20,000", "₹18k", "25000/month" -> [low, high]; null when the salary has no numbers
const parseSalaryRange = (salary) => {
  const numbers = (String(salary || '').replace(/,/g, '').match(/\d+(?:\.\d+)?\s*k?/gi) || [])
    .map((n) => (/k$/i.test(n.trim()) ? parseFloat(n) * 1000 : parseFloat(n)))
    .filter((n) => n >= 500); // ignore stray small numbers like "2 years"
  return numbers.length ? [Math.min(...numbers), Math.max(...numbers)] : null;
};

const matchesSalary = (job, min, max) => {
  if (!min && !max) return true;
  const range = parseSalaryRange(job.salary);
  if (!range) return false;
  return (!min || range[1] >= Number(min)) && (!max || range[0] <= Number(max));
};

// Does a job pass every filter except the one named in `skip`? (used for the option counts)
const jobMatches = (job, filters, skip) => {
  if (filters.keyword.trim() && !KEYWORD_FIELDS.some((f) => includesText(job[f], filters.keyword))) return false;
  for (const { name } of TEXT_FILTERS) {
    if (name !== skip && filters[name].trim() && !includesText(job[name], filters[name])) return false;
  }
  return skip === 'salary' || matchesSalary(job, filters.salaryMin, filters.salaryMax);
};

const SORTS = {
  newest: { label: 'Newest first', fn: (a, b) => Number(b.id || 0) - Number(a.id || 0) },
  salaryHigh: { label: 'Salary: high to low', fn: (a, b) => (parseSalaryRange(b.salary)?.[1] || 0) - (parseSalaryRange(a.salary)?.[1] || 0) },
  salaryLow: { label: 'Salary: low to high', fn: (a, b) => (parseSalaryRange(a.salary)?.[0] || Infinity) - (parseSalaryRange(b.salary)?.[0] || Infinity) }
};

const HighlightMatch = ({ text, query }) => {
  const q = query.trim();
  const at = q ? text.toLowerCase().indexOf(q.toLowerCase()) : -1;
  if (at < 0) return text;
  return <>{text.slice(0, at)}<b className="text-yellow-700">{text.slice(at, at + q.length)}</b>{text.slice(at + q.length)}</>;
};

// Type-to-search picker: typing filters the options (with job counts); any typed text also works as a "contains" filter
const SearchableFilter = ({ label, name, placeholder, value, options, onChange }) => {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const listRef = useRef(null);
  const q = value.trim().toLowerCase();
  const matches = options.filter((o) => !q || o.value.toLowerCase().includes(q)).slice(0, 60);

  const choose = (option) => { onChange(name, option.value); setOpen(false); };
  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActive((i) => Math.min(i + 1, matches.length - 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    if (e.key === 'Enter' && open && matches[active]) { e.preventDefault(); choose(matches[active]); }
    if (e.key === 'Escape') setOpen(false);
  };
  useEffect(() => { listRef.current?.children[active]?.scrollIntoView({ block: 'nearest' }); }, [active]);

  return (
    <div className="relative">
      <label htmlFor={`jobs-filter-${name}`} className="block text-xs font-semibold">{label}</label>
      <div className="relative mt-1">
        <input
          id={`jobs-filter-${name}`}
          value={value}
          onChange={(e) => { onChange(name, e.target.value); setOpen(true); setActive(0); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          autoComplete="off"
          role="combobox"
          aria-expanded={open}
          className={`w-full rounded-lg border px-3 py-2 pr-8 text-sm outline-none transition focus:border-yellow-400 focus:ring-2 focus:ring-yellow-200 ${value ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300 bg-white'}`}
        />
        {value ? (
          <button type="button" onClick={() => onChange(name, '')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-black" aria-label={`Clear ${label}`}>✕</button>
        ) : (
          <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400">▾</span>
        )}
      </div>
      {open && (
        <ul ref={listRef} role="listbox" className="absolute z-30 mt-1 max-h-60 w-full overflow-y-auto rounded-xl border border-yellow-200 bg-white py-1 shadow-xl">
          {matches.length === 0 ? (
            <li className="px-3 py-2 text-xs text-gray-500">No exact option. Jobs containing &ldquo;{value}&rdquo; are shown.</li>
          ) : matches.map((o, i) => (
            <li
              key={o.value}
              role="option"
              aria-selected={i === active}
              onMouseDown={(e) => { e.preventDefault(); choose(o); }}
              onMouseEnter={() => setActive(i)}
              className={`flex cursor-pointer items-center justify-between gap-2 px-3 py-1.5 text-sm ${i === active ? 'bg-yellow-100' : ''}`}
            >
              <span className="truncate"><HighlightMatch text={o.value} query={value} /></span>
              <span className="shrink-0 rounded-full bg-gray-100 px-2 text-[11px] font-bold text-gray-500">{o.count}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const Jobs = ({ openRegisterModal }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(initialFilters);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('newest');

  useEffect(() => {
    let isMounted = true;

    const fetchJobs = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/jobs`);
        if (isMounted) {
          setJobs(res.data || []);
        }
      } catch {
        if (isMounted) {
          setJobs([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchJobs();

    return () => {
      isMounted = false;
    };
  }, []);

  const setFilter = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters(initialFilters);
    setCurrentPage(1);
  };

  // Options for each picker, counted against the jobs that match every other active filter
  const optionsFor = (field) => {
    // one suggestion per value regardless of capitals/spaces ("ACCOUNTANT" and "Accountant " are the same)
    const counts = new Map();
    jobs.forEach((job) => {
      const v = String(job[field] || '').trim().replace(/\s+/g, ' ');
      if (!v || !jobMatches(job, filters, field)) return;
      const key = v.toLowerCase();
      const entry = counts.get(key) || { value: v, count: 0 };
      entry.count += 1;
      counts.set(key, entry);
    });
    return [...counts.values()].sort((a, b) => a.value.localeCompare(b.value));
  };

  const filteredJobs = jobs.filter((job) => jobMatches(job, filters)).sort(SORTS[sortBy].fn);

  const activeChips = [
    filters.keyword.trim() && { key: 'keyword', label: `"${filters.keyword.trim()}"`, clear: () => setFilter('keyword', '') },
    ...TEXT_FILTERS.filter(({ name }) => filters[name].trim()).map(({ name, label }) => ({ key: name, label: `${label}: ${filters[name].trim()}`, clear: () => setFilter(name, '') })),
    (filters.salaryMin || filters.salaryMax) && {
      key: 'salary',
      label: `Salary: ${filters.salaryMin ? `₹${Number(filters.salaryMin).toLocaleString('en-IN')}` : 'any'} – ${filters.salaryMax ? `₹${Number(filters.salaryMax).toLocaleString('en-IN')}` : 'any'}`,
      clear: () => setFilters((prev) => ({ ...prev, salaryMin: '', salaryMax: '' }))
    }
  ].filter(Boolean);

  const hasActiveFilters = Object.values(filters).some(Boolean);
  const totalPages = Math.max(1, Math.ceil(filteredJobs.length / JOBS_PER_PAGE));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const paginatedJobs = filteredJobs.slice(
    (safeCurrentPage - 1) * JOBS_PER_PAGE,
    safeCurrentPage * JOBS_PER_PAGE
  );

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const goToPage = (page) => {
    setCurrentPage(Math.min(Math.max(page, 1), totalPages));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <div className="min-h-screen bg-white text-black py-4 md:py-6">
        <div className="w-full max-w-[1600px] mx-auto">
          <section className="rounded-[16px] border border-yellow-200 bg-white shadow overflow-hidden">
            <div className="grid grid-cols-1 md:grid-cols-[320px_minmax(0,1fr)]">
              {/* Sidebar */}
              <aside className="bg-white border-r border-yellow-100 p-6 flex flex-col gap-4 min-h-[calc(100vh-32px)]">
                <h2 className="text-xl font-bold text-yellow-700 mb-2">Find the right opening</h2>
                <p className="text-xs text-gray-500 mb-2">Type in any box to search. Pick from the suggestions or just keep typing.</p>
                {TEXT_FILTERS.map((f) => (
                  <SearchableFilter key={f.name} {...f} value={filters[f.name]} options={optionsFor(f.name)} onChange={setFilter} />
                ))}

                <div>
                  <span className="block text-xs font-semibold">Salary range (₹ per month)</span>
                  <div className="mt-1 flex items-center gap-2">
                    <input
                      inputMode="numeric"
                      value={filters.salaryMin}
                      onChange={(e) => setFilter('salaryMin', e.target.value.replace(/\D/g, '').slice(0, 7))}
                      placeholder="Min"
                      aria-label="Minimum salary"
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-200"
                    />
                    <span className="text-gray-400">–</span>
                    <input
                      inputMode="numeric"
                      value={filters.salaryMax}
                      onChange={(e) => setFilter('salaryMax', e.target.value.replace(/\D/g, '').slice(0, 7))}
                      placeholder="Max"
                      aria-label="Maximum salary"
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-200"
                    />
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {SALARY_PRESETS.map((preset) => {
                      const on = filters.salaryMin === preset.min && filters.salaryMax === preset.max;
                      return (
                        <button
                          key={preset.label}
                          type="button"
                          onClick={() => { setFilters((prev) => ({ ...prev, salaryMin: on ? '' : preset.min, salaryMax: on ? '' : preset.max })); setCurrentPage(1); }}
                          className={`rounded-full border px-2.5 py-1 text-[11px] font-bold transition ${on ? 'border-yellow-500 bg-yellow-400 text-black' : 'border-yellow-200 bg-white text-yellow-700 hover:bg-yellow-50'}`}
                        >
                          {preset.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <button onClick={clearFilters} className="mt-2 rounded bg-black px-4 py-2 text-yellow-400 font-bold hover:bg-gray-900 transition disabled:opacity-50" disabled={!hasActiveFilters}>Clear Filters</button>
                <button onClick={openRegisterModal} className="mt-2 rounded bg-black px-4 py-2 text-yellow-400 font-bold hover:bg-gray-900 transition">Register for Jobs</button>
              </aside>
              {/* Main Content */}
              <main className="p-8 bg-[#fcfcfc] min-h-[calc(100vh-32px)]">
                {/* Top Bar */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
                  <div>
                    <h1 className="text-lg font-bold tracking-wide text-yellow-700 mb-1">Current Job Openings</h1>
                  </div>
                  <div className="flex gap-4">
                    <div className="bg-white rounded-xl px-4 py-2 flex flex-col items-center border border-yellow-100">
                      <span className="text-xs text-gray-500">Visible Jobs</span>
                      <span className="text-xl font-bold text-yellow-700">{filteredJobs.length}</span>
                    </div>
                    <div className="bg-white rounded-xl px-4 py-2 flex flex-col items-center border border-yellow-100">
                      <span className="text-xs text-gray-500">Total Jobs</span>
                      <span className="text-xl font-bold text-yellow-700">{jobs.length}</span>
                    </div>
                  </div>
                </div>
                <div className="mb-4 flex flex-col gap-3 md:flex-row">
                  <div className="relative flex-1">
                    <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-lg">🔍</span>
                    <input
                      value={filters.keyword}
                      onChange={(e) => setFilter('keyword', e.target.value)}
                      placeholder="Search jobs: title, company, place, qualification…"
                      aria-label="Search jobs"
                      className="w-full rounded-2xl border-2 border-yellow-300 bg-white py-3 pl-12 pr-10 text-base shadow-sm outline-none focus:border-yellow-500 focus:ring-4 focus:ring-yellow-100"
                    />
                    {filters.keyword && (
                      <button type="button" onClick={() => setFilter('keyword', '')} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-black" aria-label="Clear search">✕</button>
                    )}
                  </div>
                  <select
                    value={sortBy}
                    onChange={(e) => { setSortBy(e.target.value); setCurrentPage(1); }}
                    aria-label="Sort jobs"
                    className="rounded-2xl border-2 border-yellow-200 bg-white px-4 py-3 text-sm font-bold text-yellow-800 outline-none focus:border-yellow-500"
                  >
                    {Object.entries(SORTS).map(([key, sort]) => <option key={key} value={key}>{sort.label}</option>)}
                  </select>
                </div>

                {activeChips.length > 0 && (
                  <div className="mb-5 flex flex-wrap items-center gap-2">
                    {activeChips.map((chip) => (
                      <button key={chip.key} type="button" onClick={chip.clear} className="inline-flex items-center gap-1.5 rounded-full bg-yellow-400 px-3 py-1 text-xs font-bold text-black hover:bg-yellow-300">
                        {chip.label} <span aria-hidden="true">✕</span>
                      </button>
                    ))}
                    <button type="button" onClick={clearFilters} className="text-xs font-bold text-gray-500 underline hover:text-black">Clear all</button>
                  </div>
                )}

                {loading ? (
                  <div className="text-center py-12 text-lg font-semibold">Loading jobs...</div>
                ) : filteredJobs.length === 0 ? (
                  <div className="py-12 text-center">
                    <p className="text-lg font-semibold text-gray-500">No jobs match your search.</p>
                    {hasActiveFilters && <button type="button" onClick={clearFilters} className="mt-3 rounded-full bg-black px-5 py-2 text-sm font-bold text-yellow-400 hover:bg-gray-900">Clear all filters</button>}
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                      {paginatedJobs.map((job) => (
                        <div key={job._id} className="rounded-2xl border border-yellow-200 bg-white shadow p-5 flex flex-col justify-between min-h-[320px] relative">
                          {/* Category Badge */}
                          {job.category && <span className="absolute top-4 right-4 bg-yellow-100 text-yellow-700 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">{job.category}</span>}
                          <div className="mb-2">
                            <h3 className="text-lg font-bold text-yellow-700 mb-1 uppercase tracking-wide">{job.title}</h3>
                            <div className="grid grid-cols-2 gap-2 text-xs mb-2">
                              <div><span className="font-semibold">Location</span><br/>{job.location || '-'}</div>
                              <div><span className="font-semibold">District</span><br/>{job.district || '-'}</div>
                              <div><span className="font-semibold">Experience</span><br/>{job.experience || '-'}</div>
                              <div><span className="font-semibold">Salary</span><br/>{job.salary || '-'}</div>
                              <div><span className="font-semibold">Qualification</span><br/>{job.qualification || 'Not specified'}</div>
                              <div><span className="font-semibold">Gender</span><br/>{job.gender || 'Any'}</div>
                              <div><span className="font-semibold">Age Limit</span><br/>{job.age_limit || 'Not specified'}</div>
                            </div>
                            {job.company_firm_organisation && <div className="text-xs text-gray-700 mb-2"><span className="font-semibold">Company / Firm / Organisation</span><br/>{job.company_firm_organisation}</div>}
                            {job.description && <div className="text-xs text-gray-600 mb-2">{job.description}</div>}
                          </div>
                          <button onClick={openRegisterModal} className="mt-auto rounded bg-black px-4 py-2 text-yellow-400 font-bold hover:bg-gray-900 transition w-full">
                            Register Now
                          </button>
                        </div>
                      ))}
                    </div>
                    {/* Pagination */}
                    <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-between">
                      <span className="text-sm text-gray-500">
                        Showing {(safeCurrentPage - 1) * JOBS_PER_PAGE + 1}–{Math.min(safeCurrentPage * JOBS_PER_PAGE, filteredJobs.length)} of {filteredJobs.length} jobs · Page {safeCurrentPage} of {totalPages}
                      </span>
                      <div className="flex flex-wrap items-center justify-center gap-1.5">
                        {[
                          { label: '« First', page: 1, disabled: safeCurrentPage === 1 },
                          { label: '‹ Prev', page: safeCurrentPage - 1, disabled: safeCurrentPage === 1 }
                        ].map((btn) => (
                          <button key={btn.label} type="button" onClick={() => goToPage(btn.page)} disabled={btn.disabled} className={pagerButtonCls}>{btn.label}</button>
                        ))}
                        {getPageWindow(safeCurrentPage, totalPages).map((n, i) => (
                          n === '…' ? (
                            <span key={`gap-${i}`} className="px-1 font-bold text-yellow-700">…</span>
                          ) : (
                            <button
                              key={n}
                              type="button"
                              onClick={() => goToPage(n)}
                              aria-current={n === safeCurrentPage ? 'page' : undefined}
                              className={`min-w-[40px] rounded-full border px-3 py-2 text-sm font-bold transition ${n === safeCurrentPage ? 'border-yellow-400 bg-yellow-400 text-black shadow-md' : 'border-yellow-200 bg-white text-yellow-700 hover:bg-yellow-50'}`}
                            >
                              {n}
                            </button>
                          )
                        ))}
                        {[
                          { label: 'Next ›', page: safeCurrentPage + 1, disabled: safeCurrentPage === totalPages },
                          { label: 'Last »', page: totalPages, disabled: safeCurrentPage === totalPages }
                        ].map((btn) => (
                          <button key={btn.label} type="button" onClick={() => goToPage(btn.page)} disabled={btn.disabled} className={pagerButtonCls}>{btn.label}</button>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </main>
            </div>
          </section>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Jobs;
